import React, {useMemo} from 'react';
import {Table, Empty, Button} from 'antd';
import {ScenarioExecutionGroupFileDetailDTO} from 'hemwb-api';
import styles from './ScenarioExecutionQueue.module.scss';
import {fullName} from '../../../store/user/utils';
import {Link} from 'react-router-dom';
import {getUrlRoute, MRAppRoutes} from '../../router/routes';
import {useExecutionStatusUpdate} from '../../../websocket/useExecutionStatusUpdate';
import InstanceExecutionStatus from './InstanceExecutionStatus';
import type {ColumnsType} from 'antd/es/table';

const RestrictedAccessText = () => <span className={styles.restricted}>Restricted access</span>;

const TO_LOCALE_DATE_STRING_OPTIONS = {
  year: 'numeric' as const,
  month: 'short' as const,
  day: 'numeric' as const,
  hour: 'numeric' as const,
  minute: '2-digit' as const,
  hour12: true as const,
};

export type ScenarioInstanceListProps = {
  scenarioExecutionInstance: ScenarioExecutionGroupFileDetailDTO[];
};

const ScenarioInstanceList: React.FC<ScenarioInstanceListProps> = ({scenarioExecutionInstance}) => {
  useExecutionStatusUpdate();

  const columns: ColumnsType<ScenarioExecutionGroupFileDetailDTO> = useMemo(
    () => [
      {
        title: 'Scenario Name',
        key: 'scenarioName',
        width: '20%',
        render: (_, record) => {
          const {scenario, model} = record.executions[0];
          return scenario && model ? (
            <Link
              to={getUrlRoute(MRAppRoutes.SCENARIO_DETAIL, {
                modelUuid: model.uuid,
                scenarioId: scenario.id,
              })}
              style={{textDecoration: 'underline', color: '#000'}}
            >
              {scenario.name}
            </Link>
          ) : (
            <RestrictedAccessText />
          );
        },
      },
      {
        title: 'File Name',
        key: 'filename',
        width: '22%',
        render: (_, record) => {
          const {filename} = record.executions[0];
          return filename || <RestrictedAccessText />;
        },
      },
      {
        title: 'Model Name',
        key: 'modelName',
        width: '20%',
        render: (_, record) => {
          const {model} = record.executions[0];
          const displayName = model?.name && model.name.length > 60 ? `${model.name.substring(0, 60)}...` : model?.name;
          return model ? (
            <Link
              style={{textDecoration: 'underline', color: '#000'}}
              to={getUrlRoute(MRAppRoutes.MODEL_DETAIL, {uuid: model.uuid})}
            >
              {displayName}
            </Link>
          ) : (
            <RestrictedAccessText />
          );
        },
      },
      {
        title: 'Executed By',
        key: 'executedBy',
        width: '15%',
        render: (_, record) => {
          return record.executions[0].createdBy ? fullName(record.executions[0].createdBy) : '-';
        },
      },
      {
        title: 'Executed On',
        key: 'createdOn',
        width: '15%',
        render: (_, record) => {
          return record.executions[0].createdOn
            ? record.executions[0].createdOn.toLocaleDateString('en-US', TO_LOCALE_DATE_STRING_OPTIONS)
            : '-';
        },
      },
      {
        title: 'Status',
        key: 'status',
        width: '8%',
        render: (_, record) => <InstanceExecutionStatus execution={record.executions[0]} />,
      },
    ],
    [],
  );

  const expandedRowRender = useMemo(
    () => (record: ScenarioExecutionGroupFileDetailDTO) => {
      const {hpcJobId, hpcJobPath, mlmpJobId} = record.executions[0];
      return (
        <div style={{paddingLeft: '35px'}}>
          <div style={{marginBottom: '8px'}}>
            <b>MLMP Job ID:&nbsp;&nbsp;</b>
            <span>{mlmpJobId || '-'}</span>
          </div>
          <div style={{marginBottom: '8px'}}>
            <b>HPC Job ID:&nbsp;&nbsp;</b>
            <span>{hpcJobId || '-'}</span>
          </div>
          <div>
            <b>HPC Path Details:&nbsp;&nbsp;</b>
            <span>{hpcJobPath || '-'}</span>
          </div>
        </div>
      );
    },
    [],
  );

  if (!scenarioExecutionInstance || scenarioExecutionInstance.length === 0) {
    return <Empty description="No execution details available" style={{margin: '20px'}} />;
  }

  return (
    <Table<ScenarioExecutionGroupFileDetailDTO>
      columns={columns}
      showHeader={false}
      dataSource={scenarioExecutionInstance}
      rowKey={(record) => record.executions[0].id.toString()}
      pagination={false}
      size="small"
      expandable={{
        expandedRowRender: expandedRowRender,
        expandIcon: ({expanded, onExpand, record}) => {
          const {scenario, model} = record.executions[0];
          // Don't show expand icon for restricted rows
          if (!scenario || !model) {
            return null;
          }
          return (
            <Button
              type="text"
              size="small"
              onClick={(e) => {
                onExpand(record, e);
              }}
            >
              {expanded ? 'v' : '>'}
            </Button>
          );
        },
      }}
      className={styles.instanceListTable}
      style={{width: '100%'}}
      scroll={{x: 'max-content'}}
    />
  );
};

export default ScenarioInstanceList;
